import { useState } from 'react';
import { BulkSidebarShell, DangerZoneCard } from '../../components/bulkSidebar';
import { Button } from '../../catalyst/button';
import { Input } from '../../catalyst/input';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { usePlatformData } from '../../hooks/usePlatformData';

export function ClusterBulkSidebar({
  selectedIds,
  onDeselectAll,
  categoryName,
  pending,
  onReclassify,
  onRelabel,
  onRemove,
  canReclassify,
}: {
  selectedIds: Set<string>;
  onDeselectAll: () => void;
  categoryName: string | undefined;
  pending: 'reclassify' | 'relabel' | 'remove' | null;
  onReclassify: () => void;
  onRelabel: (target: string) => void;
  onRemove: () => void;
  canReclassify: boolean;
}) {
  const { categories } = usePlatformData();
  const categoryOptions = categories.data?.documentCategories ?? [];
  const [relabelTarget, setRelabelTarget] = useState('');
  const [dangerExpanded, setDangerExpanded] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const count = selectedIds.size;
  const busy = pending !== null;

  return (
    <BulkSidebarShell selectedCount={count} onDeselectAll={onDeselectAll} itemLabel="Page">
      <div className="mb-4 overflow-hidden rounded-lg bg-zinc-100 px-4 py-5 dark:bg-zinc-800 sm:p-6">
        <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-zinc-100">Member actions</h3>
        <div className="space-y-4">
          {canReclassify && (
            <Button color="blue" className="w-full" onClick={onReclassify} disabled={busy}>
              {pending === 'reclassify' ? 'Reclassifying…' : 'Reclassify selected'}
            </Button>
          )}

          <div className="space-y-2">
            <label htmlFor="cluster-relabel" className="block text-sm font-medium text-gray-700 dark:text-zinc-100">
              Relabel to category
            </label>
            <Input
              id="cluster-relabel"
              list="cluster-relabel-list"
              value={relabelTarget}
              onChange={(e) => setRelabelTarget(e.target.value)}
              placeholder="Select a category"
              autoComplete="off"
            />
            <datalist id="cluster-relabel-list">
              {categoryOptions.map((category) => (
                <option key={category} value={category} />
              ))}
            </datalist>
            <Button
              color="amber"
              className="w-full"
              onClick={() => onRelabel(relabelTarget)}
              disabled={busy || !relabelTarget || relabelTarget === categoryName}
            >
              {pending === 'relabel' ? 'Relabeling…' : 'Relabel selected'}
            </Button>
          </div>
        </div>
      </div>

      {/* DangerZoneCard defaults to admin-only — remove (a hard training-data change) stays admin-gated,
          while the surrounding sidebar (reclassify/relabel) is admin+supervisor (gated in the orchestrator). */}
      <DangerZoneCard expanded={dangerExpanded} onToggle={() => setDangerExpanded(!dangerExpanded)}>
        <Button color="red" className="w-full" onClick={() => setConfirmRemove(true)} disabled={busy}>
          {pending === 'remove' ? 'Removing…' : 'Remove from category'}
        </Button>
      </DangerZoneCard>

      <ConfirmDialog
        open={confirmRemove}
        title="Remove from category"
        message={`Remove ${count} page${count === 1 ? '' : 's'} from ${categoryName ?? 'this category'}'s training set? They stop training it on the next retrain; the pages themselves are untouched.`}
        confirmLabel="Remove"
        pendingLabel="Removing…"
        isPending={pending === 'remove'}
        onClose={() => setConfirmRemove(false)}
        onConfirm={() => {
          setConfirmRemove(false);
          onRemove();
        }}
      />
    </BulkSidebarShell>
  );
}
